import React, { useMemo } from 'react';
import { Card, CardContent, Typography, Box, useTheme, Stack, alpha, Skeleton, lighten } from '@mui/material';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, TooltipProps } from 'recharts';
import { ValueType, NameType } from 'recharts/types/component/DefaultTooltipContent';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

// --- Color Logic ---
// const PREDEFINED_PALETTE = [
//   '#0064FF', '#FBBC05', '#BF83FF', '#3CD856', '#FF947A', '#FA5A7D',
// ];
const PREDEFINED_PALETTE = [
  '#0064FF', '#FFA600', '#FF3023', '#67B341', '#029F8C', '#9B2B11', '#E75D0F', '#AE46BC',
  '#180273','#5B733D', '#6C63C8', '#8C8C8C'
];

const stringToColor = (str: string): string => {
  let hash = 0;
  str.split('').forEach(char => {
    hash = char.charCodeAt(0) + ((hash << 5) - hash);
  });
  let color = '#';
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xFF;
    color += `00${value.toString(16)}`.slice(-2);
  }
  return color;
};

// --- Interfaces and Custom Components ---
interface LlmSplitDataBE {
  model: string;
  value: number; 
  percentage: number;
}

interface LlmSplitChartProps {
  splitData?: LlmSplitDataBE[];
  isLoading?: boolean;
}

interface CustomTooltipContentProps extends TooltipProps<ValueType, NameType> {
  modelInfo: { [key: string]: { name: string; color: string } };
}

const CustomTooltip = ({ active, payload, modelInfo }: CustomTooltipContentProps) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const iconColor = modelInfo[data.name]?.color || '#ccc';

    return (
      <Box
        sx={{
          backgroundColor: alpha('#FFFFFF', 1),
          color: '#000000',
          padding: '8px 12px',
          borderRadius: '8px',
          boxShadow: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontFamily:'Inter'
        }}
      >
        <FiberManualRecordIcon sx={{ color: iconColor, fontSize: '12px' }} />
        <Typography  sx={{ fontWeight: 400, fontSize:'12px', color:'#666666' }}>{data.name}</Typography>
        <Typography  sx={{ fontWeight: 500, fontSize:'12px', color:'#333333' }}>{`${data.value.toFixed(2)}%`}</Typography>
      </Box>
    );
  }
  return null;
};


const LlmSplitChart: React.FC<LlmSplitChartProps> = ({ splitData, isLoading }) => {
  const theme = useTheme();

  const chartData = useMemo(() => {
    if (!splitData) return [];
    return splitData.map(model => ({
      name: model.model,
      value: model.percentage,
    }));
  }, [splitData]);
  
  const modelInfo = useMemo(() => {
    const info: { [key: string]: { name: string; color: string } } = {};
    chartData.forEach((item, index) => {
      const color = index < PREDEFINED_PALETTE.length
        ? PREDEFINED_PALETTE[index]
        : stringToColor(item.name);
      info[item.name] = { name: item.name, color };
    });
    return info;
  }, [chartData]);
  
  const legendItems = useMemo(() => {
      return Object.values(modelInfo);
  }, [modelInfo]);

  return (
    <Card elevation={0} sx={{ borderRadius: '16px', height: '100%' }}>
      <CardContent sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" component="h3" sx={{ fontWeight: '600', fontSize: '16px', mr: 2 , width:'200px'}}>
              LLM Split
            </Typography>
            {/* --- MODIFICATION STARTS HERE --- */}
            <Stack
              direction="row"
              spacing={2.5}
              alignItems="center"
              sx={{
                //maxWidth: '60%',
                minWidth:0,
                overflowX: 'auto',
                overflowY: 'hidden',
                flexWrap: 'nowrap',
                pb: 1, // Padding to make space for the scrollbar
                '::-webkit-scrollbar': { height: '4px' },
                '::-webkit-scrollbar-thumb': { backgroundColor: '#d1d5db', borderRadius: '2px' },
              }}
            >
                {legendItems.map((item) => (
                <Box key={item.name} sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                    <FiberManualRecordIcon sx={{ color: item.color, fontSize: '10px', mr: 0.75 }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                      {item.name}
                    </Typography>
                </Box>
                ))}
            </Stack>
            {/* --- MODIFICATION ENDS HERE --- */}
        </Box>
        
        <Box
            sx={{
                flexGrow: 1,
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
          {isLoading ? (
            <Skeleton variant="circular" width={280} height={280} />
          ) : (
            <ResponsiveContainer width={280} height={280}>
                <PieChart>
                    <defs>
                      {legendItems.map((item) => (
                        <linearGradient key={`gradient-${item.name}`} id={`gradient-${item.name}`}>
                          <stop offset="0%" stopColor={lighten(item.color, 0.25)} />
                          <stop offset="100%" stopColor={item.color} />
                        </linearGradient>
                      ))}
                    </defs>
                    <Pie
                        data={chartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius="100%"
                        innerRadius="45%"
                        dataKey="value"
                        paddingAngle={chartData.some(d => d.value > 0) ? 2 : 0}
                        cornerRadius={5}
                        startAngle={90}
                        endAngle={-270}
                    >
                    {chartData.map((entry) => (
                        <Cell
                            key={`cell-${entry.name}`}
                            fill={`url(#gradient-${entry.name})`}
                            stroke={theme.palette.background.paper}
                            strokeWidth={3}
                        />
                    ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip modelInfo={modelInfo} />} cursor={{ fill: 'transparent' }} />
                </PieChart>
            </ResponsiveContainer>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default LlmSplitChart;

// import React, { useMemo } from 'react';
// import { Card, CardContent, Typography, Box, useTheme, Stack, alpha, Skeleton, lighten } from '@mui/material';
// import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, TooltipProps } from 'recharts';
// import { ValueType, NameType } from 'recharts/types/component/DefaultTooltipContent';
// import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

// // --- Color Logic ---
// const PREDEFINED_PALETTE = [
//   '#0064FF', '#FBBC05', '#BF83FF', '#3CD856', '#FF947A', '#FA5A7D',
// ];

// const stringToColor = (str: string): string => {
//   let hash = 0;
//   str.split('').forEach(char => {
//     hash = char.charCodeAt(0) + ((hash << 5) - hash);
//   });
//   let color = '#';
//   for (let i = 0; i < 3; i++) {
//     const value = (hash >> (i * 8)) & 0xFF;
//     color += `00${value.toString(16)}`.slice(-2);
//   }
//   return color;
// };

// // --- Interfaces and Custom Components ---
// interface LlmSplitDataBE {
//   model: string;
//   value: number; 
//   percentage: number;
// }

// interface LlmSplitChartProps {
//   splitData?: LlmSplitDataBE[];
//   isLoading?: boolean;
// }

// interface CustomTooltipContentProps extends TooltipProps<ValueType, NameType> {
//   modelInfo: { [key: string]: { name: string; color: string } };
// }

// const CustomTooltip = ({ active, payload, modelInfo }: CustomTooltipContentProps) => {
//   if (active && payload && payload.length) {
//     const data = payload[0].payload;
//     const iconColor = modelInfo[data.name]?.color || '#ccc';

//     return (
//       <Box
//         sx={{
//           backgroundColor: alpha('#FFFFFF', 1),
//           color: '#000000',
//           padding: '8px 12px',
//           borderRadius: '8px',
//           boxShadow: 3,
//           display: 'flex',
//           alignItems: 'center',
//           gap: 1,
//           fontFamily:'Inter'
//         }}
//       >
//         <FiberManualRecordIcon sx={{ color: iconColor, fontSize: '12px' }} />
//         <Typography  sx={{ fontWeight: 400, fontSize:'12px', color:'#666666' }}>{data.name}</Typography>
//         <Typography  sx={{ fontWeight: 500, fontSize:'12px', color:'#333333' }}>{`${data.value.toFixed(2)}%`}</Typography>
//       </Box>
//     );
//   }
//   return null;
// };


// const LlmSplitChart: React.FC<LlmSplitChartProps> = ({ splitData, isLoading }) => {
//   const theme = useTheme();

//   const chartData = useMemo(() => {
//     if (!splitData) return [];
//     return splitData.map(model => ({
//       name: model.model,
//       value: model.percentage,
//     }));
//   }, [splitData]);
  
//   const modelInfo = useMemo(() => {
//     const info: { [key: string]: { name: string; color: string } } = {};
//     chartData.forEach((item, index) => {
//       const color = index < PREDEFINED_PALETTE.length
//         ? PREDEFINED_PALETTE[index]
//         : stringToColor(item.name);
//       info[item.name] = { name: item.name, color };
//     });
//     return info;
//   }, [chartData]);
  
//   const legendItems = useMemo(() => {
//       return Object.values(modelInfo);
//   }, [modelInfo]);

//   return (
//     <Card elevation={0} sx={{ borderRadius: '16px', height: '100%' }}>
//       <CardContent sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
//         <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
//             <Typography variant="h6" component="h3" sx={{ fontWeight: '600', fontSize: '16px' }}>
//               LLM Split
//             </Typography>
//             <Stack direction="row" spacing={2} alignItems="center">
//                 {legendItems.map((item) => (
//                 <Box key={item.name} sx={{ display: 'flex', alignItems: 'center' }}>
//                     <FiberManualRecordIcon sx={{ color: item.color, fontSize: '10px', mr: 0.75 }} />
//                     <Typography variant="caption" sx={{ color: 'text.secondary' }}>
//                     {item.name}
//                     </Typography>
//                 </Box>
//                 ))}
//             </Stack>
//         </Box>
        
//         <Box
//             sx={{
//                 flexGrow: 1,
//                 width: '100%',
//                 display: 'flex',
//                 alignItems: 'center',
//                 justifyContent: 'center',
//             }}
//         >
//           {isLoading ? (
//             <Skeleton variant="circular" width={280} height={280} />
//           ) : (
//             <ResponsiveContainer width={280} height={280}>
//                 <PieChart>
//                     {/* STEP 1: Define a gradient for each legend item */}
//                     <defs>
//                       {legendItems.map((item) => (
//                         <linearGradient key={`gradient-${item.name}`} id={`gradient-${item.name}`}>
//                           <stop offset="0%" stopColor={lighten(item.color, 0.25)} />
//                           <stop offset="100%" stopColor={item.color} />
//                         </linearGradient>
//                       ))}
//                     </defs>
//                     <Pie
//                         data={chartData}
//                         cx="50%"
//                         cy="50%"
//                         labelLine={false}
//                         outerRadius="100%"
//                         innerRadius="45%"
//                         dataKey="value"
//                         paddingAngle={chartData.some(d => d.value > 0) ? 2 : 0}
//                         cornerRadius={5}
//                         startAngle={90}
//                         endAngle={-270}
//                     >
//                     {chartData.map((entry) => (
//                         <Cell
//                             key={`cell-${entry.name}`}
//                             // STEP 2: Apply the gradient fill to each cell
//                             fill={`url(#gradient-${entry.name})`}
//                             stroke={theme.palette.background.paper}
//                             strokeWidth={3}
//                         />
//                     ))}
//                     </Pie>
//                     <Tooltip content={<CustomTooltip modelInfo={modelInfo} />} cursor={{ fill: 'transparent' }} />
//                 </PieChart>
//             </ResponsiveContainer>
//           )}
//         </Box>
//       </CardContent>
//     </Card>
//   );
// };

// export default LlmSplitChart;